import React, { useEffect, useState } from "react";
import { filter, includes, map } from "lodash";
import styled from "styled-components";
import { FontWeight, transparentize } from "@glossy";
import { content, dominant } from "@glossy/theme";
import { tr } from "@linkfluence/i18n";
import {
  Panels,
  PeriodId,
  PeriodMath,
  PeriodType,
  Range,
} from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Milestone } from "@linkfluence/models/milestone";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import PeriodRange from "@ui-kit/period-selector/range";
import { getCurrentMilestone, getCurrentPeriod, getPanel } from "./data";

type Period = Omit<Range, "interval">;
type Panel = typeof Panels[keyof typeof Panels] | undefined;

interface IProps {
  period: Period;
  timezone: Timezone;
  locale: Locale;
  onChange: (period: Period) => void;
  limits?: Period;
  milestones: Milestone[];
  customPeriod?: boolean;
  customHour?: boolean;
  opened?: boolean;
  onClose?: () => void;
  allowCancel?: boolean;
  className?: string;
  getClassicPeriodLabel?: (id: PeriodId) => string;
  allowBeyondLimit?: boolean;
  showOnlyBasicPeriods?: boolean;
  hiddenPeriods?: PeriodType[];
  limitTo100Days?: boolean;
}

const Root = styled.div`
  display: flex;
`;

const Option = styled.div<{
  isCustom?: boolean;
  isSelected?: boolean;
}>`
  color: ${(props) =>
    props.isSelected ? dominant(props) : content(props, "primary")};
  font-size: 14px;
  font-weight: ${FontWeight.Normal};
  padding: 8px 16px;
  border: 0;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: ${(props) => props.isCustom && "100%"};
  background-color: ${(props) =>
    props.isSelected && transparentize(0.8, dominant(props))};
  &:hover {
    background-color: ${(props) =>
      props.isSelected
        ? transparentize(0.8, dominant(props))
        : transparentize(0.85, content(props, "primary"))};
  }
`;

const SubMenu = styled.div<{ isAlone: boolean }>`
  flex: ${(props) => (props.isAlone ? 1 : 0)} 0
    ${(props) => (props.isAlone ? "auto" : "240px")};
  min-width: 240px;
`;

const CustomOption = styled(Option)<{ isSelected?: boolean }>`
  background-color: ${(props) =>
    props.isSelected && transparentize(0.8, dominant(props) as string)};
`;

const OptionPanel = styled.div`
  border-left: 1px solid ${(props) => content(props, "tertiary")};
  flex: 1 0 auto;
  min-width: 201px;
`;

const CalendarPanel = styled.div`
  padding: 24px;
`;

const usePeriodSelector = (
  period: Period,
  milestones: Milestone[],
  opened?: boolean
) => {
  const [prevPanel, setPrevPanel] = useState<Panel>(undefined);
  const [currentPanel, setPanel] = useState<Panel>(undefined);
  const currentPeriod = getCurrentPeriod(period);
  const currentMilestone = getCurrentMilestone(
    currentPeriod,
    period,
    milestones
  );
  const classicPeriods = filter(PeriodMath, { type: PeriodType.Classic });
  const rollingPeriods = filter(PeriodMath, { type: PeriodType.Rolling });
  const isCalendar =
    currentPanel === Panels.Calendar || currentPanel === Panels.CalendarFrom;

  useEffect(() => {
    if (opened) {
      const newPanel = getPanel(currentPeriod, currentMilestone);
      setPanel(newPanel);
      setPrevPanel(newPanel);
    } else {
      setPanel(undefined);
    }
  }, [currentMilestone, currentPeriod, opened]);

  const onChangePanel = (panel: Panel) => {
    if (panel === currentPanel) {
      return setPanel(getPanel(currentPeriod, currentMilestone));
    }
    return setPanel(panel);
  };

  return {
    classicPeriods,
    currentMilestone,
    currentPanel,
    currentPeriod,
    isCalendar,
    onChangePanel,
    prevPanel,
    rollingPeriods,
  };
};

const UiKitPeriodSelector = ({
  customPeriod,
  customHour,
  period,
  limits,
  milestones = [],
  locale,
  timezone,
  opened,
  onChange,
  onClose,
  allowCancel,
  className,
  getClassicPeriodLabel = (id) => `DATES::${id}`,
  allowBeyondLimit = false,
  hiddenPeriods = [],
  limitTo100Days,
}: IProps) => {
  const {
    classicPeriods,
    currentMilestone,
    currentPanel,
    currentPeriod,
    isCalendar,
    onChangePanel,
    prevPanel,
    rollingPeriods,
  } = usePeriodSelector(period, milestones, opened);
  return (
    <Root className={className}>
      <SubMenu isAlone={!currentPanel}>
        {!includes(hiddenPeriods, PeriodType.Classic) &&
          map(classicPeriods, (classicPeriod) => (
            <Option
              isSelected={
                classicPeriod.id === currentPeriod && currentPanel === undefined
              }
              key={classicPeriod.id}
              onClick={() => onChange(classicPeriod)}
            >
              {tr(locale, getClassicPeriodLabel(classicPeriod.id as PeriodId))}
            </Option>
          ))}
        {!includes(hiddenPeriods, PeriodType.Rolling) && (
          <Option
            isSelected={currentPanel === Panels.Rolling}
            onClick={() => onChangePanel(Panels.Rolling)}
          >
            {tr(locale, "DATES::ROLLING")}
            <Icon glyph="chevron-right" size={12} />
          </Option>
        )}
        {!includes(hiddenPeriods, PeriodType.From) && (
          <Option
            isSelected={currentPanel === Panels.CalendarFrom}
            onClick={() => onChangePanel(Panels.CalendarFrom)}
          >
            {tr(locale, "DATES::CUSTOM-FROM")}
            <Icon glyph="chevron-right" size={12} />
          </Option>
        )}
        {customPeriod && !includes(hiddenPeriods, PeriodType.Custom) && (
          <>
            <CustomOption
              isSelected={currentPanel === Panels.Calendar}
              onClick={() => onChangePanel(Panels.Calendar)}
            >
              {tr(locale, "DATES::CUSTOM::LABEL")}{" "}
              <Icon glyph="chevron-right" size={12} />
            </CustomOption>
          </>
        )}
        {milestones.length > 0 &&
          !includes(hiddenPeriods, PeriodType.Milestones) && (
            <Option
              isSelected={currentPanel === Panels.Milestones}
              onClick={() => onChangePanel(Panels.Milestones)}
            >
              {tr(locale, "DATES::MILESTONES")}
              <Icon glyph="chevron-right" size={12} />
            </Option>
          )}
      </SubMenu>
      {!!currentPanel && (
        <OptionPanel>
          {isCalendar && (
            <CalendarPanel>
              <PeriodRange
                limits={limits as Period}
                locale={locale}
                period={period || { from: null, to: null }}
                timezone={timezone}
                customHour={customHour}
                onClose={onClose}
                onChange={onChange}
                allowCancel={allowCancel}
                allowFromOnly={currentPanel === Panels.CalendarFrom}
                isNewCalendar={prevPanel !== currentPanel}
                allowBeyondLimit={allowBeyondLimit}
                limitTo100Days={limitTo100Days}
              />
            </CalendarPanel>
          )}
          {currentPanel === Panels.Rolling &&
            map(rollingPeriods, (rollingPeriod) => (
              <Option
                isSelected={rollingPeriod.id === currentPeriod}
                key={rollingPeriod.id}
                onClick={() => onChange(rollingPeriod)}
              >
                {tr(locale, `DATES::${rollingPeriod.id}`)}
              </Option>
            ))}
          {currentPanel === Panels.Milestones &&
            map(milestones, (milestone) => (
              <Option
                key={milestone.id}
                isSelected={milestone.id === currentMilestone?.id}
                onClick={() =>
                  onChange({
                    from: milestone.startDate,
                    to: milestone.endDate,
                  })
                }
              >
                {milestone.name}
              </Option>
            ))}
        </OptionPanel>
      )}
    </Root>
  );
};

export default UiKitPeriodSelector;
